// Site translations (i18next). One JSON file per page per language in
// src/locales/<lang>/<page>.json, plus common.json (header, menu, footer).
// common.json is bundled for every language so the Layout never waits; page
// files load lazily with the page. Missing keys fall back to English.
//
// URL scheme: ?lang=<code> (e.g. /tasmi/?lang=ar). GitHub Pages serves one
// folder per route, so a query string needs no new routes or shells, and each
// language still has its own crawlable URL, announced with hreflang in Layout.
// Order: ?lang, then the saved choice, then the browser, then English.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en/common.json';
import ar from './locales/ar/common.json';
import ur from './locales/ur/common.json';
import fr from './locales/fr/common.json';
import es from './locales/es/common.json';
import id from './locales/id/common.json';
import tr from './locales/tr/common.json';
import ms from './locales/ms/common.json';

export const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'ar', name: 'العربية', rtl: true },
    { code: 'ur', name: 'اردو', rtl: true },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'id', name: 'Bahasa Indonesia' },
    { code: 'tr', name: 'Türkçe' },
    { code: 'ms', name: 'Bahasa Melayu' },
];
const CODES = LANGUAGES.map((l) => l.code);
export const STORAGE_KEY = 'preferred-language';
// Routes whose page is fully translated; only these get hreflang alternates.
export const TRANSLATED_ROUTES = ['/tasmi', '/', '/prayer-times', '/calendar', '/qibla', '/alexa-skill', '/api', '/ai-agents', '/faq-support', '/niyyah', '/mobile-app', '/quran'];

export const isRtl = (code) => LANGUAGES.some((l) => l.code === code && l.rtl);
export const supported = (code) => (CODES.includes(code) ? code : null);

function initialLanguage() {
    try {
        const q = supported(new URLSearchParams(window.location.search).get('lang'));
        if (q) return q;
        const saved = supported(window.localStorage.getItem(STORAGE_KEY));
        if (saved) return saved;
    } catch (e) { /* storage blocked: fall through */ }
    return supported((navigator.language || 'en').split('-')[0]) || 'en';
}

const lazyPages = {
    type: 'backend',
    init() {},
    read(lng, ns, cb) {
        import(`./locales/${lng}/${ns}.json`).then((m) => cb(null, m.default), (err) => cb(err, null));
    },
};

function applyDocument(lng) {
    document.documentElement.lang = lng;
    document.documentElement.dir = isRtl(lng) ? 'rtl' : 'ltr';
}

i18n.on('languageChanged', applyDocument);

i18n.use(lazyPages).use(initReactI18next).init({
    lng: initialLanguage(),
    fallbackLng: 'en',
    supportedLngs: CODES,
    ns: ['common'],
    defaultNS: 'common',
    resources: {
        en: { common: en }, ar: { common: ar }, ur: { common: ur }, fr: { common: fr },
        es: { common: es }, id: { common: id }, tr: { common: tr }, ms: { common: ms },
    },
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    returnNull: false,
});
applyDocument(i18n.language);

export default i18n;
